import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from '@blocs.i18n';
import MeatballMenu from '@minecraft.meatball-menu';
import DropdownItem from '@minecraft.dropdown-item';
import Icon from '@minecraft.icon';
import CloudinaryDocument from '@minecraft.cloudinary-document';
import { getEnvironment } from '@minecraft.environment';
import { Directions, ID } from '../../../shared/types';

const StyledMeatballMenuWrap = styled.div`
  position: absolute;
  right: 0;
  top: 0.4rem;
`;

const StyledMeatballMenu = styled(MeatballMenu)`
  width: 1rem;
  height: 1rem;
`;

const StyledDropdownItem = styled(DropdownItem)`
  align-items: center;
  display: flex;
`;

const StyledOrderIcon = styled(Icon)`
  margin-right: 0.5rem;
  width: 0.75rem;
  height: 0.75rem;
`;

type Props = {
  isFastCastProfile?: boolean;
  submissionHasAddInNote?: boolean;
  isSubmissionCustomized?: boolean;
  isPublic?: boolean;
  guid?: string;
  roleGuid?: string;
  projectGuid?: string;
  profileId: ID;
  projectId?: number;
  roleId: ID;
  handleViewAddInNotesModalOpen?: () => void;
  handleEditAddInNotesModalOpen?: () => void;
  handleDeleteSubmission?: () => void;
  handleReorder?: (direction: Directions) => void;
  menuSettings: {
    isDeletable?: boolean;
    profileLink?: boolean;
    sizeSheet?: boolean;
    downloadSnapshot?: boolean;
    reorderDirections?: Directions[];
  };
  snapshotKey?: string;
  snapShotStartOffset?: string;
  isVideoSnapshot?: boolean;
};

const TalentCardMenu = ({
  isFastCastProfile = false,
  submissionHasAddInNote = false,
  isSubmissionCustomized,
  profileId,
  roleId,
  handleViewAddInNotesModalOpen,
  handleEditAddInNotesModalOpen,
  handleDeleteSubmission,
  menuSettings: {
    isDeletable = false,
    profileLink = false,
    sizeSheet = false,
    downloadSnapshot = false,
    reorderDirections,
  } = {},
  snapshotKey,
  isVideoSnapshot = false,
  snapShotStartOffset,
  isPublic,
  guid,
  roleGuid,
  projectGuid,
  projectId,
  handleReorder,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const roleIdParam = isSubmissionCustomized ? `?roleId=${roleId}` : '';
  const pathname = isPublic
    ? `${getEnvironment().BASE_NAME}/profile-public/${guid}`
    : `${getEnvironment().BASE_NAME}/profile/${profileId}/preview${roleIdParam}`;
  const areAddInNoteOptionsVisible = isFastCastProfile && submissionHasAddInNote;
  const sizeSheetLinkUrl = useMemo(() => {
    const commonUrlSection = `/talent/${guid}/role/${roleGuid}/project/${projectGuid || projectId}`;

    return `${getEnvironment().BASE_NAME}/${isPublic ? 'public-' : ''}talent-size-sheet${commonUrlSection}${
      roleId ? `?roleId=${roleId}` : ''
    }`;
  }, [guid, roleGuid, projectGuid, projectId, roleId, isPublic]);

  return (
    <StyledMeatballMenuWrap>
      <StyledMeatballMenu disabled={!profileId} qaId="talent-card-toggle-menu">
        {downloadSnapshot && snapshotKey && (
          <CloudinaryDocument.Download
            cloudName={getEnvironment().CN_HOT_CLOUDNAME}
            fileKey={snapshotKey}
            isVideoSnapshot={isVideoSnapshot}
            snapShotStartOffset={snapShotStartOffset}
            customComponent={({ url }): JSX.Element => (
              <a href={url} className="dropdown-item" target="_blank" rel="noopener noreferrer">
                {t('common:talentCard.snapshot.download')}
              </a>
            )}
          />
        )}
        {profileLink && (
          <DropdownItem
            onClick={() => window.open(pathname, '_blank', 'noopener,noreferrer')}
            className="dropdown-item"
          >
            {t('common:talentCard.viewProfile')}
          </DropdownItem>
        )}
        {areAddInNoteOptionsVisible && (
          <>
            <DropdownItem onClick={handleViewAddInNotesModalOpen}>{t('common:label.viewNotes')}</DropdownItem>
            <DropdownItem onClick={handleEditAddInNotesModalOpen}>{t('common:label.editAddInNotes')}</DropdownItem>
          </>
        )}
        {sizeSheet && (
          <DropdownItem
            onClick={() => window.open(sizeSheetLinkUrl, '_blank', 'noopener,noreferrer')}
            className="dropdown-item"
          >
            {t('common:talentCard.viewSizeSheet')}
          </DropdownItem>
        )}
        {isDeletable && (
          <DropdownItem onClick={handleDeleteSubmission} data-qa-id="talent-card-remove-from-group">
            {t('common:talentCard.group.removeTalent')}
          </DropdownItem>
        )}
        {reorderDirections && handleReorder && (
          <>
            {reorderDirections.includes('top') && (
              <StyledDropdownItem onClick={() => handleReorder('top')} data-qa-id="move-to-top-button">
                <StyledOrderIcon name="first-place" />
                {t('common:talentCard.reorder.moveToTop')}
              </StyledDropdownItem>
            )}
            {reorderDirections.includes('up') && (
              <StyledDropdownItem onClick={() => handleReorder('up')} data-qa-id="move-up-one-button">
                <StyledOrderIcon name="chevron-open-thick" />
                {t('common:talentCard.reorder.moveUpOne')}
              </StyledDropdownItem>
            )}
            {reorderDirections.includes('down') && (
              <StyledDropdownItem onClick={() => handleReorder('down')} data-qa-id="move-down-one-button">
                <StyledOrderIcon name="chevron-closed-thick" />
                {t('common:talentCard.reorder.moveDownOne')}
              </StyledDropdownItem>
            )}
            {reorderDirections.includes('bottom') && (
              <StyledDropdownItem onClick={() => handleReorder('bottom')} data-qa-id="move-to-bottom-button">
                <StyledOrderIcon name="last-place" />
                {t('common:talentCard.reorder.moveToBottom')}
              </StyledDropdownItem>
            )}
          </>
        )}
      </StyledMeatballMenu>
    </StyledMeatballMenuWrap>
  );
};

export default TalentCardMenu;
